import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  ViewChild, ViewContainerRef,
} from '@angular/core';
import { Router } from '@angular/router';
import { FaIconLibrary } from '@fortawesome/angular-fontawesome';
import { faBook, faCircleQuestion } from '@fortawesome/free-solid-svg-icons';
import { DropDownFilterSettings } from '@progress/kendo-angular-dropdowns';
import {
  SVGIcon,
  bellIcon,
  logoutIcon,
  menuIcon,
  questionCircleIcon,
  searchIcon,
} from '@progress/kendo-svg-icons';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { orderBy, some } from 'lodash';
import {
  BehaviorSubject,
  catchError,
  filter,
  map,
  of,
  take,
  takeUntil,
} from 'rxjs';
import { AuthService } from 'src/app/services/auth.service';
import { AbstractComponent } from 'src/app/shared/components/abstract-component';
import { referenceDataFilterSettings } from 'src/app/shared/constants/reference-data-filter-settings';
import { Location } from 'src/app/shared/models/location';
import { User } from 'src/app/shared/models/user';
import { UserRole } from 'src/app/shared/models/user-role';
import { AuthStore } from 'src/app/stores/auth.store';
import { Permissions } from 'src/app/shared/enums/permissions';
import { Permission } from 'src/app/shared/models/permission';
import { faBell, faCircle } from '@fortawesome/free-solid-svg-icons';
import { Constants } from 'src/app/shared/constants/constants';
import { SelectEvent, TabAlignment } from '@progress/kendo-angular-layout';
import { PopoverAnchorDirective } from '@progress/kendo-angular-tooltip';
import { MarkingWarnService } from 'src/app/modules/dashboard/services/marking-warn.service';
import { DialogCloseResult } from '@progress/kendo-angular-dialog';
import { DashboardService } from 'src/app/modules/dashboard/services/dashboard.service';
import { AllNotificationsResponse, NotificationSummaryResponse } from 'src/app/modules/dashboard/interface/get-all-notifications-response';
import { NotificationService } from '@progress/kendo-angular-notification';
import {
  maxBadgeCountLimit,
  NotificationAction,
  NotificationReadAllBit,
  NotificationType,
} from 'src/app/shared/enums/notification';
import { dialogAnimationDuration, dialogTimer } from 'src/app/shared/constants/dialog-specs';
import { MarkPayload } from 'src/app/modules/dashboard/interface/mark-payload';
import { MarkReadNotificationService } from 'src/app/modules/dashboard/services/notification.service';
import { formatNotificationDateTime } from 'src/app/shared/functions/format-notifications-datetime';
import { AppConfigService } from 'src/app/services/app-config.service';
import { IAppConfig } from 'src/app/shared/interfaces/app-config';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class HeaderComponent extends AbstractComponent implements OnInit {
  public menuIcon: SVGIcon = menuIcon;
  public searchIcon: SVGIcon = searchIcon;
  public bellIcon: SVGIcon = bellIcon;
  public questionCircleIcon: SVGIcon = questionCircleIcon;
  public logoutIcon: SVGIcon = logoutIcon;
  public kendokaAvatar: string =
    'https://www.telerik.com/kendo-angular-ui-develop/components/navigation/appbar/assets/kendoka-angular.png';

  public show: boolean = false;
  public user: User;
  role: UserRole;
  roles: UserRole[];
  location: Location;
  locations: Location[];
  agencies: Location[];
  filterSettings: DropDownFilterSettings = referenceDataFilterSettings;

  public alignment: TabAlignment = 'start';
  @ViewChild('anchor', { static: false })
  private anchor: PopoverAnchorDirective;
  selectedTabIndex: number = 0;
  allNotifications: AllNotificationsResponse[] = [];
  actionRequired: AllNotificationsResponse[] = [];
  informational: AllNotificationsResponse[] = [];
  zeroUnreadActionReqZeroUnreadInfo: boolean = false;
  zeroUnreadActionReqOnePlusUnreadInfo: boolean = false;
  onePlusUnreadActionReq: boolean = false;
  badgeCountToShow: number | null = null;
  private emptyCount: number = 0;
  private dotBadgeCount: number = 0;
  private actionRequiredTab: number = 0;
  private tabIndexOne: number = 1;
  private startIndexPortion: number = 0;
  private endIndexPortion: number = 10;
  public actionRequiredLength: number = 0;
  public informationalLength: number = 0;
  public loadSpinner: boolean = false;

  showNewReferralModal$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);
  isNewReferral: boolean = true;
  enableNotifications: boolean = true;
  isPopoverOpen: boolean = false;
  showNonProdBanner: boolean = false;
  nonProdEnvironmentLabel: string = 'LOWER ENVIRONMENT';

  constructor(
    public authStore: AuthStore,
    private authService: AuthService,
    public router: Router,
    private cdRef: ChangeDetectorRef,
    private oidcSecurityService: OidcSecurityService,
  library: FaIconLibrary,
  private vcr: ViewContainerRef,
  private markingWarnService: MarkingWarnService,
    private dashboardService: DashboardService,
    private notificationService: NotificationService,
    private markReadNotificationService: MarkReadNotificationService,
    private appConfigService: AppConfigService
  ) {
    super();
    library.addIcons(faCircleQuestion, faBook, faBell, faCircle);
  }

  ngOnInit(): void {
    this.appConfigService.configStatus
      .pipe(
        takeUntil(this.componentDestroyed),
        filter((isLoaded: boolean) => isLoaded)
      )
      .subscribe(() => {
        this.setEnvironmentBannerState();
        this.cdRef.detectChanges();
      });

    this.authStore.UserInfo$.pipe(
      filter((user: User) => !!user),
      // eslint-disable-next-line @typescript-eslint/no-magic-numbers
      take(1)
    ).subscribe((user: User) => {
      this.user = { ...user };
      this.cdRef.detectChanges();
      if (this.enableNotifications) {
        this.startPolling();
      }
    });

    // Once a notification is marked as read, start polling again to refresh the list.
    this.markReadNotificationService.notificationMarked$.subscribe((isView ) => {
      this.startPolling();
      if(isView){
        this.anchor.hide();
        this.isPopoverOpen = false; 
      }
    });
  }

  logout(): void {
    this.authService.logout();
  }

  login(): void {
    this.authService.startAuthentication();
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  override afterOnDestroy(): void {}

  openHelp(): void {
    this.router.navigate(['/help-links']);
  }

  openNewReferralModalHandler(): void {
    this.showNewReferralModal$.next(true);
  }

  openNotification(): void {
    this.router.navigate(['/notification']);
  }

  closeModalHandler(): void {
    this.showNewReferralModal$.next(false);
  }

  isShowTopSearchBar(): boolean {
    return some(
      this.user?.permissions,
      (x: Permission) => x && x.permissionId === Permissions.CanViewGlobalSearch
    );
  }

  openReleaseNotes(): void {
    const url: string = this.user?.email?.includes('doeexternal')
      ? Constants.releaseNotes.DOE_EXTERNAL
      : Constants.releaseNotes.NON_DOE_EXTERNAL;
    window.open(url, '_blank');
  }

  private setEnvironmentBannerState(): void {
    const config: IAppConfig = this.appConfigService.getConfig();
    const environmentName: string = (config?.environment ?? '').trim().toUpperCase();

    this.showNonProdBanner = environmentName !== 'PROD';
    this.nonProdEnvironmentLabel = environmentName || 'LOWER ENVIRONMENT';

    if (this.showNonProdBanner) {
      document.body.classList.add('non-prod-env');
      return;
    }

    document.body.classList.remove('non-prod-env');
  }

  // Get all notifications and sift through them to get action required and informational ones.
  // As well as which ones are unread/read.
  startPolling(): void {
    this.cdRef.detectChanges();
    this.loadSpinner = true;
    this.dashboardService
      .getNotificationSummary()
      .pipe(
        takeUntil(this.componentDestroyed), // remove comp destroy when above code is uncommented
        map((response: NotificationSummaryResponse) => {
          // Map the response as before.
          const result: AllNotificationsResponse[] = orderBy(
            response?.notificationResponse || [],
            ['dateTime'],
            ['desc']
          );

          // Retrieve action required notifcations
          const actionRequired: AllNotificationsResponse[] = result.filter(
            (item: AllNotificationsResponse) =>
              item.type === NotificationType.ActionRequired
          );

          // Retrieve informational notifications
          const informational: AllNotificationsResponse[] = result.filter(
            (item: AllNotificationsResponse) =>
              item.type === NotificationType.Informational
          );
          // this.getBadgeCount(actionRequired, informational);
          this.badgeCountToShow = response.totalNotificationCount;
          this.actionRequiredLength = response.totalActionRequiredCount;
          this.informationalLength = response.totalInformationalCount;
          // this.getTabCount(actionRequired, informational);
           result.forEach((item: AllNotificationsResponse) => {
            item.dateTime = formatNotificationDateTime(
              new Date(item.dateTime), // Date/time from the backend
              new Date() // todays Date
            );
          });
          this.allNotifications = result;
          return { actionRequired, informational };
        }),
        catchError(() => {
          return of({ actionRequired: [], informational: [] }); // Return an empty observable in case of error to keep the polling going.
        })
      )
      .subscribe({
        next: ({
          actionRequired,
          informational,
        }: {
          actionRequired: AllNotificationsResponse[];
          informational: AllNotificationsResponse[];
        }) => {
          this.loadSpinner = false;
          this.actionRequired = actionRequired
            // .filter(
            //   (item: AllNotificationsResponse) =>
            //     item.actions === NotificationAction.UnRead ||
            //     item.actions === NotificationAction.Created //show only actions Unread=2/ Created=5.
            // )
            // .slice(this.startIndexPortion, this.endIndexPortion); // Limit to 10 items only.
          this.informational = informational 
          // .slice(
          //   this.startIndexPortion,
          //   this.endIndexPortion
          // ); // Limit to 10 items.
          this.cdRef.detectChanges();
        },
        error: () => {
          this.loadSpinner = false;
          this.notificationService.show({
            content: Constants.NOTIFICATION_ERROR_MESSAGE,
            cssClass: 'notification-error',
            position: { horizontal: 'center', vertical: 'top' },
            animation: {
              type: 'slide',
              duration: dialogAnimationDuration,
            },
            type: { style: 'error', icon: true },
          });
        },
      });
  }

  markingNotification(payload: MarkPayload[]): void {
    this.loadSpinner = true;
    this.dashboardService
      .markNotification(payload)
      .pipe(takeUntil(this.componentDestroyed))
      .subscribe({
        next: () => {
          this.loadSpinner = false;
          this.startPolling(); // Refresh notifications after marking.
          this.notificationService.show({
            content: 'Marking notifications successful.',
            cssClass: 'notification-success',
            hideAfter: dialogTimer,
            position: { horizontal: 'center', vertical: 'top' },
            animation: { type: 'slide', duration: 60 },
            type: { style: 'success', icon: true },
          });
        },
        error: () => {
          this.loadSpinner = false;
          this.notificationService.show({
            content: 'Marking notifications failed. Please try again later.',
            cssClass: 'notification-error',
            position: { horizontal: 'center', vertical: 'top' },
            animation: { type: 'slide', duration: 60 },
            type: { style: 'error', icon: true },
          });
        },
      });
  }

  markAllAsDone(selectedTabIndex: number): void {
    const sourceArray = selectedTabIndex === this.actionRequiredTab ? this.actionRequired : selectedTabIndex === this.tabIndexOne
      ? this.informational : [];

    const markAboveAsReadPayload: MarkPayload[] = sourceArray.map(
      (item: AllNotificationsResponse) => ({
        notificationSk: item.notificationSk,
        studentSk: item.studentSK,
        notificationTypeId: item.notificationTypeId,
        notificationStatus: NotificationAction.Read,
        markAllRead: NotificationReadAllBit.All,
        authUserRoleLocationSk: item.authUserRoleLocationSk
      })
    );
    if (selectedTabIndex === this.actionRequiredTab) {
      this.markingWarnService
        .open('markAbove', this.vcr)
         .pipe(take(1))
        .subscribe((result: DialogCloseResult | any) => {
          if (!(result instanceof DialogCloseResult)) {
            this.markingNotification(markAboveAsReadPayload);
          }
        });
    }
    if (selectedTabIndex === this.tabIndexOne) {
      this.markingNotification(markAboveAsReadPayload);
    }
  }

  togglePopover(): void {
   if (!this.isPopoverOpen) this.startPolling();
  this.isPopoverOpen = !this.isPopoverOpen;

    this.anchor.toggle();
  }

  viewAllNotifications(): void {
    this.router.navigate(['/all-notifications']);
    this.togglePopover();
  }

  onSelect(event: SelectEvent): void {
    this.selectedTabIndex = event.index;
  }

  getBadgeCount(
    actionReqNotification: AllNotificationsResponse[],
    infoNotification: AllNotificationsResponse[]
  ): void {
    // Retrieve the count of unread action required notifications
    const unreadActionReqCount: number = actionReqNotification.filter(
      (item: AllNotificationsResponse) =>
        item.actions === NotificationAction.UnRead ||
        item.actions === NotificationAction.Created
    ).length;

    // Retrieve the count of unread informational notifications
    const unreadInfoCount: number = infoNotification.filter(
      (item: AllNotificationsResponse) =>
        item.actions === NotificationAction.UnRead ||
        item.actions === NotificationAction.Created
    ).length;

    // If both counts are zero, no badge is shown
    if (
      unreadActionReqCount === this.emptyCount &&
      unreadInfoCount === this.emptyCount
    ) {
      this.badgeCountToShow = null; // No badge
    }
    // If no unread action required but some unread informational, show a dot badge
    else if (
      unreadActionReqCount === this.emptyCount &&
      unreadInfoCount > this.emptyCount
    ) {
      this.badgeCountToShow = this.dotBadgeCount;
    } // If there are unread action required notifications, show their count (capped at 99)
    else if (unreadActionReqCount > this.emptyCount) {
      this.badgeCountToShow = Math.min(
        unreadActionReqCount,
        maxBadgeCountLimit
      ); // Limit to 99
    }
  }

  getTabCount(
    actionReqNotification: AllNotificationsResponse[],
    infoNotification: AllNotificationsResponse[]
  ): void {
    this.actionRequiredLength = actionReqNotification.filter(
      (item: AllNotificationsResponse) =>
        item.actions === NotificationAction.UnRead ||
        item.actions === NotificationAction.Created //show only actions Unread=2/ Created=5.
    ).length;
    this.informationalLength = infoNotification.length;
  }
}
